import React, { useCallback } from 'react';
import { canDelete, canUpdate } from 'utils/permissions';
import { DataTableRowAction, DataTableRowActions } from '@utmost.co/utmost-design-system';
import { tokens } from '@beeline.com/bp-design-system';
import { EngagementJobProfile } from './jobProfiles.mutations';
import { useJobProfilesIntl } from './translations/useJobProfilesIntl';
import { useJobProfileData } from './useJobProfileData';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';

interface Props {
  onDeleteAll: (uids: string[]) => void;
  setSelectedJobProfile: (resource: EngagementJobProfile) => void;
  jobProfile: EngagementJobProfile;
  refetch: () => void;
}

export const JobProfileTableActions: React.FC<Props> = ({
  onDeleteAll,
  setSelectedJobProfile,
  jobProfile,
  refetch
}) => {
  const intl = useJobProfilesIntl();

  const canUpdateJobProfile = canUpdate(jobProfile?.permissions?.JOB_PROFILE);
  const canDeleteJobProfile = canDelete(jobProfile?.permissions?.JOB_PROFILE);
  const { update } = useJobProfileData();
  const { showError } = useSnackbar();

  const handleUpdateJobProfileAssignable = useCallback(async () => {
    try {
      await update({
        jobProfile: {
          uid: jobProfile.uid,
          assignable: !jobProfile.assignable
        }
      });
      refetch();
    } catch (err) {
      showError(intl.failedToUpdateJobProfile);
    }
  }, [jobProfile, showError, update, refetch, intl]);

  return (
    <DataTableRowActions disabled={!canUpdateJobProfile && !canDeleteJobProfile}>
      <DataTableRowAction dataTest="job-profile-status-change" onClick={handleUpdateJobProfileAssignable}>
        {intl.assignableStatusLabel({ assignable: String(jobProfile?.assignable) })}
      </DataTableRowAction>
      {canUpdateJobProfile && (
        <DataTableRowAction dataTest="edit-profile-mi" onClick={() => setSelectedJobProfile(jobProfile)}>
          {intl.edit}
        </DataTableRowAction>
      )}
      {canDeleteJobProfile && (
        <DataTableRowAction
          style={{ color: tokens.colorAlert }}
          dataTest="delete-profile-mi"
          onClick={() => onDeleteAll([jobProfile.uid])}
        >
          {intl.delete}
        </DataTableRowAction>
      )}
    </DataTableRowActions>
  );
};
